/**
 * useInactivityTimeout — disconnessione automatica per inattività (sicurezza PC condivisi).
 *
 * Traccia l'attività reale dell'utente (mouse, tastiera, scroll, touch). Dopo `timeoutMin` minuti
 * SENZA interazione esegue `onTimeout` (tipicamente logout + redirect a /login). `warningSec` secondi
 * prima mostra un avviso con countdown, da cui l'utente può restare connesso (`keepAlive`).
 *
 * - Cross-tab: l'ultimo istante di attività è in localStorage, quindi l'attività in QUALSIASI scheda
 *   tiene viva la sessione in tutte. Il controllo è a polling (ogni few secondi), così legge sempre
 *   il valore condiviso aggiornato.
 * - Allineato ai session cookie: alla chiusura del browser il cookie muore comunque; qui resettiamo
 *   l'ultimo-attività al mount (l'apertura dell'app conta come attività).
 *
 * Config: VITE_INACTIVITY_TIMEOUT_MIN (default 120 = 2h), VITE_INACTIVITY_WARNING_SEC (default 60).
 * `timeoutMin` può essere passato come getter per allinearsi a un valore live (es. la policy
 * configurata server-side, esposta da `GET /auth/me` come `sessionIdleMinutes`).
 */
import { ref, onMounted, onUnmounted, type Ref } from 'vue'

const STORAGE_KEY = 'immigreer:lastActivity'
/**
 * Scritto dal PORTALE CLASSICO (view/layout/template.xhtml, repo legacy) a ogni page-load che
 * rinnova il token (throttle 5'). In produzione legacy e Vue sono same-origin (Apache) →
 * localStorage condiviso: l'attività sul legacy conta come attività, altrimenti il timer
 * sloggherebbe (revocando il refresh!) un utente che sta lavorando sul portale classico.
 */
const LEGACY_REFRESH_KEY = 'immigreer:lastTokenRefresh'
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const
// Poll ADATTIVO: rado quando l'utente è attivo (basso overhead), fitto solo durante il countdown
// di avviso (per un conto alla rovescia fluido). Il controllo "normale" viene inoltre schedulato per
// scattare esattamente all'inizio dell'avviso, così l'avviso non parte in ritardo.
const POLL_IDLE_MS = 15_000     // controllo normale (rado)
const POLL_WARNING_MS = 1_000   // durante l'avviso: countdown al secondo
const ACTIVITY_THROTTLE_MS = 5_000  // non riscrivere lastActivity a ogni pixel

function envInt(key: string, fallback: number): number {
  const raw = (import.meta.env as Record<string, string | undefined>)[key]
  const n = raw != null ? Number(raw) : NaN
  return Number.isFinite(n) && n > 0 ? n : fallback
}

export interface InactivityOptions {
  /** Callback eseguita allo scadere dell'inattività (es. logout + redirect login). */
  onTimeout: () => void | Promise<void>
  /**
   * Minuti di inattività prima del logout. Può essere un numero fisso o un getter (`() => number`),
   * valutato a ogni giro del loop di polling: usa un getter per allineare il timer al valore live
   * di configurazione (es. `auth.me?.sessionIdleMinutes`), disponibile solo dopo il fetch di /auth/me
   * e potenzialmente aggiornato senza remount del componente. Default: VITE_INACTIVITY_TIMEOUT_MIN o 120.
   */
  timeoutMin?: number | (() => number | undefined)
  /** Secondi di preavviso (countdown) prima del logout. Default: VITE_INACTIVITY_WARNING_SEC o 60. */
  warningSec?: number
}

export interface InactivityHandle {
  /** True quando è in corso il countdown di preavviso. */
  warningVisible: Ref<boolean>
  /** Secondi mancanti al logout durante il preavviso. */
  secondsLeft: Ref<number>
  /** "Resta connesso": azzera l'inattività e nasconde l'avviso. */
  keepAlive: () => void
}

export function useInactivityTimeout(opts: InactivityOptions): InactivityHandle {
  const fallbackTimeoutMin = envInt('VITE_INACTIVITY_TIMEOUT_MIN', 120)

  /** Risolve i minuti di timeout correnti: legge il getter live se fornito, altrimenti il numero fisso. */
  function currentTimeoutMin(): number {
    const raw = typeof opts.timeoutMin === 'function' ? opts.timeoutMin() : opts.timeoutMin
    return Number.isFinite(raw) && (raw as number) > 0 ? (raw as number) : fallbackTimeoutMin
  }

  const warningMs = (opts.warningSec ?? envInt('VITE_INACTIVITY_WARNING_SEC', 60)) * 1_000

  const warningVisible = ref(false)
  const secondsLeft = ref(0)

  let pollTimer: number | undefined
  let lastWrite = 0
  let firedTimeout = false

  function now(): number { return Date.now() }

  function readLastActivity(): number {
    const a = Number(localStorage.getItem(STORAGE_KEY) || 0)
    // Attività sul portale classico (same-origin in prod; in dev origini diverse → 0, innocuo)
    const l = Number(localStorage.getItem(LEGACY_REFRESH_KEY) || 0)
    const v = Math.max(Number.isFinite(a) ? a : 0, Number.isFinite(l) ? l : 0)
    return v > 0 ? v : now()
  }

  function markActivity(): void {
    const t = now()
    // throttle: scrivo al massimo ogni ACTIVITY_THROTTLE_MS
    if (t - lastWrite < ACTIVITY_THROTTLE_MS) return
    lastWrite = t
    localStorage.setItem(STORAGE_KEY, String(t))
  }

  function keepAlive(): void {
    lastWrite = 0
    localStorage.setItem(STORAGE_KEY, String(now()))
    warningVisible.value = false
    secondsLeft.value = 0
  }

  function scheduleNext(delayMs: number): void {
    pollTimer = window.setTimeout(loop, delayMs)
  }

  function loop(): void {
    const timeoutMs = currentTimeoutMin() * 60_000
    const idle = now() - readLastActivity()
    if (idle >= timeoutMs) {
      if (firedTimeout) return
      firedTimeout = true
      stop()
      void opts.onTimeout()
      return
    }
    const warnStart = timeoutMs - warningMs
    if (idle >= warnStart) {
      // Finestra di avviso: countdown fluido (poll a 1s).
      warningVisible.value = true
      secondsLeft.value = Math.max(0, Math.ceil((timeoutMs - idle) / 1000))
      scheduleNext(POLL_WARNING_MS)
    } else {
      // Attivo: poll rado, ma non oltre l'inizio dell'avviso → l'avviso parte puntuale.
      if (warningVisible.value) warningVisible.value = false
      const msUntilWarning = warnStart - idle
      scheduleNext(Math.max(POLL_WARNING_MS, Math.min(POLL_IDLE_MS, msUntilWarning)))
    }
  }

  function stop(): void {
    if (pollTimer) { clearTimeout(pollTimer); pollTimer = undefined }
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActivity))
  }

  onMounted(() => {
    // L'apertura dell'app conta come attività (evita un logout immediato su lastActivity stantio).
    keepAlive()
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActivity, { passive: true }))
    scheduleNext(POLL_IDLE_MS)
  })

  onUnmounted(stop)

  return { warningVisible, secondsLeft, keepAlive }
}
